/**
 * File Explorer sidebar variant.
 *
 * Replaces the boards list inside the persistent Sidebar when the
 * "Files" contour is active. Stacks:
 *   - Path breadcrumb (current root)
 *   - Search box
 *   - Recursive tree
 *
 * Drag-drop is handled at the panel level — any file dropped onto the
 * panel triggers an upload into the currently-selected directory (or the
 * scope root if nothing's expanded). A confirm dialog opens when the
 * filename already exists.
 *
 * The panel is also reused as a popover inside Card Detail's Chat tab —
 * see `<FilePickerPopover>`. There the panel runs in "picker mode": tree
 * clicks resolve the queued promise instead of opening a viewer.
 */
import type { FsSearchHit, FsTreeNode } from '@chaos/types/fs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Folder, RefreshCw, Upload } from 'lucide-react';
import * as React from 'react';

import { fsApi, fsKeys } from '../../lib/api/fs';
import { cn } from '../../lib/utils';
import { useFileExplorerStore } from '../../stores/file-explorer-store';
import { ConfirmOverwriteDialog } from './confirm-overwrite-dialog';
import { FileSearch } from './file-search';
import { FileTree } from './file-tree';
import { UploadStatusBar } from './upload-status';

interface FileExplorerPanelProps {
  pickerMode?: boolean;
  onFileSelected?: (node: FsTreeNode) => void;
}

export function FileExplorerPanel({ pickerMode = false, onFileSelected }: FileExplorerPanelProps) {
  const qc = useQueryClient();
  const selectPath = useFileExplorerStore((s) => s.selectPath);
  const setCurrentRoot = useFileExplorerStore((s) => s.setCurrentRoot);
  const currentRoot = useFileExplorerStore((s) => s.currentRoot);
  const setExpanded = useFileExplorerStore((s) => s.setExpanded);
  const expanded = useFileExplorerStore((s) => s.expanded);

  const scopesQ = useQuery({
    queryKey: fsKeys.scopes(),
    queryFn: () => fsApi.scopes(),
    staleTime: 60_000,
  });

  // Default root = first allowed scope when the user hasn't picked one yet.
  React.useEffect(() => {
    if (currentRoot) return;
    if (scopesQ.data?.scopes && scopesQ.data.scopes.length > 0) {
      setCurrentRoot(scopesQ.data.scopes[0] ?? null);
    }
  }, [scopesQ.data, currentRoot, setCurrentRoot]);

  const handleOpenFile = React.useCallback(
    (node: FsTreeNode) => {
      // In picker mode, only files resolve the picker — directories still
      // expand normally inside the tree (handled by TreeNodeRow.toggle).
      if (node.kind === 'dir') return;
      selectPath(node.path);
      onFileSelected?.(node);
    },
    [onFileSelected, selectPath],
  );

  const handleSelectHit = React.useCallback(
    (hit: FsSearchHit) => {
      selectPath(hit.path);
      // Expand the chain of parent directories so the hit is visible in
      // the tree once the user dismisses the search results.
      const parts = hit.path.split('/').filter(Boolean);
      let current = '';
      for (let i = 0; i < parts.length - 1; i++) {
        current = `${current}/${parts[i]}`;
        setExpanded(current, true);
      }
      onFileSelected?.({
        name: parts[parts.length - 1] ?? hit.path,
        path: hit.path,
        kind: 'file',
      });
    },
    [onFileSelected, setExpanded, selectPath],
  );

  // Drag-drop upload state.
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [dragging, setDragging] = React.useState(false);
  const [pendingOverwrite, setPendingOverwrite] = React.useState<{
    file: File;
    folder: string;
  } | null>(null);

  const uploadMut = useMutation({
    mutationFn: async (input: { file: File; folder: string; overwrite: boolean }) => {
      return fsApi.upload({
        folder: input.folder,
        file: input.file,
        overwrite: input.overwrite,
      });
    },
    onSuccess: async (_node, vars) => {
      // Refresh the destination directory so the new row appears.
      await qc.invalidateQueries({ queryKey: fsKeys.list(vars.folder) });
    },
  });

  const uploadTarget = currentRoot ?? '';

  const handleFiles = React.useCallback(
    (files: FileList) => {
      if (!uploadTarget) return;
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (!file) continue;
        uploadMut.mutate(
          { file, folder: uploadTarget, overwrite: false },
          {
            onError: (err) => {
              const maybe = err as { status?: number };
              if (maybe.status === 409) {
                setPendingOverwrite({ file, folder: uploadTarget });
                return;
              }
            },
          },
        );
      }
    },
    [uploadMut, uploadTarget],
  );

  const onDrop = React.useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setDragging(false);
      if (!e.dataTransfer?.files) return;
      handleFiles(e.dataTransfer.files);
    },
    [handleFiles],
  );

  const onDragOver = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.types.includes('Files')) setDragging(true);
  }, []);

  const onDragLeave = React.useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Only flip off when the leave target is the container itself —
    // otherwise hovering child rows would oscillate the state.
    if (e.currentTarget === e.target) setDragging(false);
  }, []);

  return (
    <div className="flex h-full flex-col text-[13px]">
      <ScopePicker
        scopes={scopesQ.data?.scopes ?? []}
        current={currentRoot}
        onChange={(scope) => {
          setCurrentRoot(scope);
          // Reset expansion under the previous root so we don't carry over
          // stale state when switching scopes.
          setExpanded(scope, true);
        }}
      />

      {/* Search + tree + upload all share the drop zone. */}
      {/* biome-ignore lint/a11y/noStaticElementInteractions: the upload
          button next to the tree header is the keyboard-accessible
          alternative; the div only handles drag-drop pointer events. */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={cn(
          'relative flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-2 py-2',
          dragging && 'ring-1 ring-foreground/40',
        )}
      >
        <FileSearch scope={currentRoot} onSelectHit={handleSelectHit} />

        <div className="flex items-center justify-between px-1 pt-1 text-[11px] uppercase tracking-wider text-muted-foreground/80">
          <span>Tree</span>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Upload file to current scope"
              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              title="Upload file"
            >
              <Upload className="h-3 w-3" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => {
                if (currentRoot) {
                  void qc.invalidateQueries({ queryKey: fsKeys.list(currentRoot) });
                }
              }}
              aria-label="Refresh tree"
              className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
              title="Refresh"
            >
              <RefreshCw className="h-3 w-3" aria-hidden />
            </button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = e.target.files;
            if (files && files.length > 0) handleFiles(files);
            e.target.value = '';
          }}
        />

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {currentRoot ? (
            <FileTree rootPath={currentRoot} onOpenFile={handleOpenFile} />
          ) : (
            <p className="px-2 py-2 text-[12px] text-muted-foreground">No scopes available.</p>
          )}
        </div>

        {/* Tiny drop indicator overlay; no big illustrations per UI rules. */}
        {dragging && (
          <div className="pointer-events-none absolute inset-0 flex items-end justify-center pb-3">
            <div className="rounded bg-background/95 px-3 py-1 text-[11px] text-foreground shadow">
              Drop to upload to {currentRoot ?? '…'}
            </div>
          </div>
        )}
      </div>

      <UploadStatusBar />

      {pendingOverwrite && (
        <ConfirmOverwriteDialog
          filename={pendingOverwrite.file.name}
          folder={pendingOverwrite.folder}
          onConfirm={() => {
            uploadMut.mutate(
              {
                file: pendingOverwrite.file,
                folder: pendingOverwrite.folder,
                overwrite: true,
              },
              {
                onSettled: () => setPendingOverwrite(null),
              },
            );
          }}
          onCancel={() => setPendingOverwrite(null)}
        />
      )}

      {pickerMode && (
        <p className="px-3 py-1.5 text-[11px] text-muted-foreground border-t border-border">
          Click a file to attach it.
        </p>
      )}
      {expanded && null /* suppress unused warning */}
    </div>
  );
}

interface ScopePickerProps {
  scopes: string[];
  current: string | null;
  onChange: (scope: string) => void;
}

function ScopePicker({ scopes, current, onChange }: ScopePickerProps) {
  const [open, setOpen] = React.useState(false);

  if (scopes.length === 0) {
    return (
      <header className="flex items-center gap-1.5 border-b border-border px-3 py-2 text-[12px] text-muted-foreground">
        <Folder className="h-3.5 w-3.5" aria-hidden /> No scopes
      </header>
    );
  }

  if (scopes.length === 1) {
    return (
      <header className="flex items-center gap-1.5 border-b border-border px-3 py-2 text-[12px] text-muted-foreground">
        <Folder className="h-3.5 w-3.5" aria-hidden />
        <span className="truncate" title={current ?? scopes[0]}>
          {current ?? scopes[0]}
        </span>
      </header>
    );
  }

  return (
    <header className="relative border-b border-border">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-[12px] text-foreground hover:bg-foreground/5"
      >
        <Folder className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate" title={current ?? ''}>
          {current ?? 'Select scope'}
        </span>
        {open ? (
          <ChevronDown className="h-3 w-3 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronRight className="h-3 w-3 text-muted-foreground" aria-hidden />
        )}
      </button>
      {open && (
        <ul className="absolute left-0 right-0 top-full z-30 max-h-48 overflow-y-auto border-b border-border bg-background py-1 shadow-md">
          {scopes.map((s) => (
            <li key={s}>
              <button
                type="button"
                onClick={() => {
                  onChange(s);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center px-3 py-1 text-left text-[12px] transition-colors hover:bg-foreground/5',
                  s === current ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                <span className="truncate">{s}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </header>
  );
}
